import GridViewIcon from '@mui/icons-material/GridView';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import Box from '@mui/material/Box';
import { lighten } from '@mui/material/styles';
import Icon, {
  MoleculesIcon, MoveLineBottomIcon,
  MoveLineTopIcon, SequenceFeaturesIcon,
} from 'common/components/Elements/Icon/Icon';
import Commands from 'core/components/Elements/Commands/Commands';
import { useAppSelector } from 'core/redux/store/configureStore';
import { separatorCommand } from 'core/utilities/command/command';
import type { EnhancedCommand } from 'core/utilities/core.types';
import theme from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import type { Session as SequenceAnalyserSession } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { SequenceSelection } from 'sequenceAnalyser/utilities/selection/selection';
import type {
  RemovedSequence, SequenceAnalyserVisualisation} from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { SequenceAnalyserVisualisations,
} from 'sequenceAnalyser/utilities/sequenceAnalyser.types';

function hasSelected(selection: SequenceSelection) {
  return selection.ids.length > 0;
}
function hasSelectedOneLine(selection: SequenceSelection) {
  return selection.ids.length === 1;
}
function hasSelectedResidues(selection: SequenceSelection) {
  return selection.positionStart !== undefined && selection.positionEnd !== undefined;
}

interface ComponentProps {
  removedSequences: RemovedSequence[];
  selection: SequenceSelection;
  onClick: (command: EnhancedCommand) => void;
  sessionSequenceType: SequenceType;
  session: SequenceAnalyserSession;
  sessionExpired?: boolean;
  exportToPNGError?: string;
  emptySequencesTable?: boolean;
}

function SelectionActions({
  selection, onClick, removedSequences: removedSequencesState, sessionSequenceType,
  sessionExpired, session, exportToPNGError, emptySequencesTable,
}: ComponentProps) {
  const {
    canCreateHitList, cannotCreateHitListMessage, createHitListLabel,
  } = session;

  const fileFormat = useAppSelector((state) => (
    state.configuration.configuration.table_export_import_format as 'csv' | 'tsv'
  ));

  const commands: EnhancedCommand[] = [
    {
      id: 'sort',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.order,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.orderTitle,
      iconEl: <Icon element={UnfoldMoreIcon} mr />,
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [
        {
          small: true,
          id: 'arrangeBySimilarity',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.arrangeBySimilarity,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.arrangeBySimilarityTitle,
          onClick,
          active: !sessionExpired,
          commands: [],
        },
        {
          small: true,
          id: 'sortByLabelAsc',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.sortByLabelAsc,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.sortByLabelAscTitle,
          onClick,
          active: true,
          commands: [],
        },
        {
          small: true,
          id: 'sortByLabelDesc',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.sortByLabelDesc,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.sortByLabelDescTitle,
          onClick,
          active: true,
          commands: [],
        },
        separatorCommand(),
        {
          id: 'moveTop',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.moveTop,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.moveTopTitle,
          active: hasSelected(selection),
          commands: [],
          onClick,
          iconEl: <MoveLineTopIcon mr />,
        },
        {
          id: 'moveUp',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.moveUp,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.moveUpTitle,
          active: hasSelectedOneLine(selection),
          commands: [],
          onClick,
          iconEl: <MoveLineTopIcon mr />,
        },
        {
          id: 'moveDown',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.moveDown,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.moveDownTitle,
          active: hasSelectedOneLine(selection),
          commands: [],
          onClick,
          iconEl: <MoveLineBottomIcon mr />,
        },
      ],
    },
    {
      id: 'sequence',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.sequence,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.sequenceTitle,
      iconEl: (
        <Box sx={{ display: 'inline-block', marginTop: -1, verticalAlign: 'middle' }}>
          <MoleculesIcon mr />
        </Box>
      ),
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [
        {
          id: 'removeSelection',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.removeSelection,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.removeSelectionTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        {
          id: 'openInsertRemovedSequences',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.insertRemovedSequences(
            removedSequencesState.length,
          ),
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.insertRemovedSequencesTitle,
          onClick,
          active: removedSequencesState.length > 0,
          commands: [],
        },
        separatorCommand(),
        {
          id: 'copyToClipboard',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.copyResidues,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.copyResiduesTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        {
          id: 'copyToClipboardFasta',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.copyResiduesFasta,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.copyResiduesFastaTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        {
          id: 'copyToClipboardIds',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.copyToClipboardIds,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.copyToClipboardIdsTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        // {
        //   id: 'copyToStore',
        //   label: I18n.SequenceAnalyser.TableActions.SelectionActions.copyToStore,
        //   title: I18n.SequenceAnalyser.TableActions.SelectionActions.copyToStoreTitle,
        //   onClick,
        //   active: hasSelected(selection),
        //   commands: [],
        // },
        {
          id: 'duplicate',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.duplicate,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.duplicateTitle,
          onClick,
          active: hasSelectedOneLine(selection),
          commands: [],
        },
        separatorCommand(),
        {
          id: 'openInsertFromFile',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.insertFromFile,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.insertFromFileTitle,
          onClick,
          active: !sessionExpired,
          commands: [],
        },
        // {
        //   id: 'openInsertManual',
        //   label: I18n.SequenceAnalyser.TableActions.SelectionActions.insertManual,
        //   title: I18n.SequenceAnalyser.TableActions.SelectionActions.insertManualTitle,
        //   onClick,
        //   active: true,
        //   commands: [],
        // },
        // {
        //   id: 'openInsertSessionStore',
        //   label: I18n.SequenceAnalyser.TableActions.SelectionActions.insertSessionStore,
        //   title: I18n.SequenceAnalyser.TableActions.SelectionActions.insertSessionStoreTitle,
        //   onClick,
        //   active: true,
        //   commands: [],
        // },
        ...(sessionSequenceType === SequenceType.dna
          ? [
            separatorCommand(),
            {
              id: 'oppositeStrands',
              label: I18n.SequenceAnalyser.TableActions.SelectionActions.oppositeStrands,
              title: I18n.SequenceAnalyser.TableActions.SelectionActions.oppositeStrandsTitle,
              onClick,
              active: !sessionExpired,
              commands: [],
            },
          ]
          : []),
        separatorCommand(),
        {
          id: 'openColors',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.colors,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.colorsTitle,
          onClick,
          active: true,
          commands: [],
        },
      ],
    },
    {
      id: 'feature',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.feature,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.featureTitle,
      iconEl: (
        <Box sx={{ display: 'inline-block', marginTop: -1, verticalAlign: 'middle' }}>
          <SequenceFeaturesIcon mr />
        </Box>
      ),
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [
        {
          id: 'collapseFeaturesSelected',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.collapseFeaturesSelected,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.collapseFeaturesSelectedTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        {
          id: 'expandFeaturesSelected',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.expandFeaturesSelected,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.expandFeaturesSelectedTitle,
          onClick,
          active: hasSelected(selection),
          commands: [],
        },
        {
          id: 'collapseFeaturesAll',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.collapseFeaturesAll,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.collapseFeaturesAllTitle,
          onClick,
          active: true,
          commands: [],
        },
        {
          id: 'expandFeaturesAll',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.expandFeaturesAll,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.expandFeaturesAllTitle,
          onClick,
          active: true,
          commands: [],
        },
        {
          id: 'openShowHideFeatures',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.showHideFeatures,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.showHideFeaturesTitle,
          onClick,
          active: true,
          commands: [],
        },
      ],
    },
    {
      id: 'run',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.run,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.runTitle,
      iconEl: <Icon element={ViewWeekIcon} mr />,
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [
        {
          id: 'openAlign',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.align,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.alignTitle,
          onClick,
          active: !emptySequencesTable && !sessionExpired,
          commands: [],
        },
        {
          id: 'resetAlign',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.resetAlign,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.resetAlignTitle,
          onClick,
          active: !emptySequencesTable && !sessionExpired,
          commands: [],
        },
        separatorCommand(),
        {
          id: 'openInsertGaps',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.insertGaps,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.insertGapsTitle,
          onClick,
          active: !emptySequencesTable && hasSelectedResidues(selection),
          commands: [],
        },
        {
          id: 'removeGaps',
          label: I18n.SequenceAnalyser.TableActions.SelectionActions.removeGaps,
          title: I18n.SequenceAnalyser.TableActions.SelectionActions.removeGapsTitle,
          onClick,
          active: !emptySequencesTable && hasSelected(selection),
          commands: [],
        },
      ],
    },
    {
      id: 'export',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.export,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.exportTitle,
      iconEl: <Icon element={SaveAltIcon} mr />,
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [...(!canCreateHitList && !cannotCreateHitListMessage
        ? []
        : [{
          id: 'exportHitList',
          label: `${createHitListLabel || 'Hit List'}...`,
          title: (
            cannotCreateHitListMessage
            || `${createHitListLabel || 'Hit List'}...`
          ),
          onClick,
          active: canCreateHitList,
          commands: [],
        }]), /*{
        id: 'exportPDF',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.exportPDF,
        title: I18n.SequenceAnalyser.TableActions.SelectionActions.exportPDFTitle,
        onClick,
        active: !emptySequencesTable && true,
        commands: [],
      },*/ {
        id: 'exportFile',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.exportFile,
        title: I18n.SequenceAnalyser.TableActions.SelectionActions.exportFileTitle,
        onClick,
        active: !emptySequencesTable && !sessionExpired,
        commands: [],
      }, {
        id: 'exportFeaturesCSV',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.exportFeaturesCSV(fileFormat),
        title: I18n.SequenceAnalyser.TableActions.SelectionActions.exportFeaturesCSVTitle(fileFormat),
        onClick,
        active: !emptySequencesTable && !sessionExpired,
        commands: [],
      }, {
        id: 'exportPNG',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.exportPNG,
        title: exportToPNGError || I18n.SequenceAnalyser.TableActions.SelectionActions.exportPNGTitle,
        onClick,
        active: !emptySequencesTable && exportToPNGError === undefined,
        commands: [],
      }],
    },
    {
      id: 'views',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.views,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.viewsTitle,
      iconEl: <Icon element={GridViewIcon} mr />,
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [...Object.keys(SequenceAnalyserVisualisations).map((v) => ({
        id: v,
        label: I18n.SequenceAnalyser.SettingsModal.views[v as SequenceAnalyserVisualisation],
        title: I18n.SequenceAnalyser.SettingsModal.views[v as SequenceAnalyserVisualisation],
        onClick,
        active: !sessionExpired,
        commands: [],
      })), {
        id: 'openSequencesNavigator',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.openSequencesNavigator,
        title: I18n.SequenceAnalyser.TableActions.SelectionActions.openSequencesNavigatorTitle,
        onClick,
        active: true,
        commands: [],
      }, {
        id: 'openSimilarityTree',
        label: I18n.SequenceAnalyser.TableActions.SelectionActions.openSimilarityTree,
        title: I18n.SequenceAnalyser.TableActions.SelectionActions.openSimilarityTreeTitle,
        onClick,
        active: !sessionExpired,
        commands: [],

      }],
    },
    {
      id: 'openSettings',
      label: I18n.SequenceAnalyser.TableActions.SelectionActions.settings,
      title: I18n.SequenceAnalyser.TableActions.SelectionActions.settingsTitle,
      iconEl: <Icon element={SettingsIcon} mr />,
      onClick,
      active: true,
      hideArrowIcon: true,
      commands: [],
    },
  ];

  const headerBackgroundColor = theme.palette.primary.main;
  const headerBackgroundColorHover = lighten(headerBackgroundColor, 0.15);

  return (
    <Commands
        id="SequenceAnalyserTableActions"
        commands={commands.map((c) => ({ ...c, inheritColor: true }))}
        groupFrom="right"
        hoverColor={headerBackgroundColorHover}
      />
  );
}

export default SelectionActions;
